/**
 * AuthorizationTimeline — Four-moment horizontal step indicator.
 *
 * Shows Gateway → Discovery → Invocation → Execution as circles connected
 * by lines. Collapses to single line when all green and unremarkable.
 *
 * States per circle: green (allowed), red (denied), gray (not recorded),
 * amber (step-up/conditional).
 */

import type { AuthorizationMoment } from './action-assurance-types';

const React = (globalThis as any).React;
const { useState, useCallback } = React;

export interface AuthorizationTimelineProps {
  moments: Record<string, AuthorizationMoment>;
  plugin?: string;
  className?: string;
}

const MOMENT_ORDER = ['gateway', 'discovery', 'invocation', 'execution'] as const;

const MOMENT_LABELS: Record<string, string> = {
  gateway: 'Gateway',
  discovery: 'Discovery',
  invocation: 'Invocation',
  execution: 'Execution',
};

const DECISION_COLORS: Record<string, string> = {
  allowed: 'var(--color-success, #52c41a)',
  denied: 'var(--color-error, #ff4d4f)',
  step_up: 'var(--color-warning, #faad14)',
  not_recorded: 'var(--color-border, #d9d9d9)',
};

export function AuthorizationTimeline({ moments, plugin, className }: AuthorizationTimelineProps) {
  const [expanded, setExpanded] = useState(false);

  const resolvedMoments = MOMENT_ORDER.map((name) => {
    const data = moments[name];
    return {
      name,
      label: MOMENT_LABELS[name],
      decision: data?.decision || 'not_recorded',
      at: data?.at,
    };
  });

  const allPassed = resolvedMoments.every(
    (m) => m.decision === 'allowed' || m.decision === 'not_recorded',
  );
  const hasAnyRecorded = resolvedMoments.some((m) => m.decision !== 'not_recorded');

  if (!hasAnyRecorded) return null;

  const testIdPrefix = plugin ? `${plugin}-` : '';

  // Collapsed state: all green → single line with expand affordance
  if (allPassed && !expanded) {
    return React.createElement('div', {
      'data-testid': `${testIdPrefix}authz-timeline`,
      className,
      style: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 12,
        color: 'var(--color-success, #52c41a)',
        cursor: 'pointer',
      },
      onClick: () => setExpanded(true),
      role: 'button',
      tabIndex: 0,
      'aria-label': 'Authorized at all checkpoints. Click to expand.',
      onKeyDown: (e: any) => { if (e.key === 'Enter' || e.key === ' ') setExpanded(true); },
    },
      React.createElement('span', {
        style: { width: 8, height: 8, borderRadius: '50%', backgroundColor: 'var(--color-success)' },
      }),
      React.createElement('span', null, 'Authorized at all checkpoints'),
      React.createElement('span', { style: { fontSize: 10, opacity: 0.6 } }, '▸'),
    );
  }

  // Expanded: full 4-step timeline
  const containerStyle: Record<string, string | number> = {
    display: 'flex',
    alignItems: 'center',
    gap: 0,
    padding: '8px 0',
  };

  return React.createElement('div', {
    'data-testid': `${testIdPrefix}authz-timeline`,
    className,
    style: containerStyle,
  },
    ...resolvedMoments.map((moment, idx) => {
      const color = DECISION_COLORS[moment.decision];
      const isLast = idx === resolvedMoments.length - 1;

      return React.createElement(React.Fragment, { key: moment.name },
        // Circle
        React.createElement('div', {
          'data-testid': `${testIdPrefix}authz-moment-${moment.name}`,
          style: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4,
          },
          title: `${moment.label}: ${moment.decision.replace('_', ' ')}${moment.at ? ` at ${moment.at}` : ''}`,
        },
          React.createElement('div', {
            style: {
              width: 16,
              height: 16,
              borderRadius: '50%',
              backgroundColor: color,
              border: moment.decision === 'not_recorded' ? '2px dashed var(--color-border)' : 'none',
              boxSizing: 'border-box',
            },
          }),
          React.createElement('span', {
            style: { fontSize: 10, color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' },
          }, moment.label),
        ),
        // Connecting line (except after last)
        !isLast && React.createElement('div', {
          style: {
            flex: 1,
            height: 2,
            minWidth: 24,
            backgroundColor: 'var(--color-border, #d9d9d9)',
            marginBottom: 18,
          },
        }),
      );
    }),
  );
}
